import { useCallback, useMemo } from 'react';
import { useEmployeeDetails } from '../graphql/hooks/employees/getEmployeeDetails';
import { IS_LOCAL_ENV, LOCAL_ADMIN_EMAIL } from '../constants/environment';
import { tokenManager } from '../services/tokenManager';
import { filterOfficesByUserPermissions } from '../utils/filterOfficesByUserPermissions';
import { ROLE_HIERARCHY } from '../constants/userRoles';
import { formatPrintName } from '../utils/filedReadValueFormatters';
import get from 'lodash/get';

export const useEmployeeInfo = () => {
  const {
    data: employeeDetails,
    isLoading,
    isError,
  } = useEmployeeDetails(
    IS_LOCAL_ENV ? LOCAL_ADMIN_EMAIL : tokenManager.getUserEmail()
  );

  const employeeId = get(employeeDetails, 'id', null);
  const roles = get(employeeDetails, 'roles', []);
  const officeIds = get(employeeDetails, 'officeIds', []);
  const officeAssignments = get(employeeDetails, 'officeAssignments', []);

  /**
   * Given a list of office dropdown options, return the option that matches the
   * current user's default (primary) office, or null if it cannot be resolved
   * or is not present in the list. This is the single place that knows how the
   * user's default office is derived - callers just pass their candidate options
   * and get back a ready-to-use option.
   *
   * @param {Array} officeOptions - Office dropdown options ([{ value, label, ... }])
   * @returns {Object|null} The matching office option, or null.
   */
  const getMyOfficeOption = useCallback(
    (officeOptions = []) => {
      if (!Array.isArray(officeOptions) || officeOptions.length === 0) {
        return null;
      }
      const { defaultOfficeId } = filterOfficesByUserPermissions(
        officeOptions,
        roles,
        officeAssignments,
        officeIds
      );
      if (!defaultOfficeId) return null;
      return (
        officeOptions.find((option) => option.value === defaultOfficeId) || null
      );
    },
    [roles, officeAssignments, officeIds]
  );

  /**
   * Given a list of coordinator dropdown options, return the option that
   * represents the current user, or null if the user is not one of them.
   * Presence in the list is the eligibility rule - callers never see the
   * user's id.
   *
   * @param {Array} coordinatorOptions - Coordinator options ([{ value, label, ... }])
   * @returns {Object|null} The matching coordinator option, or null.
   */
  const getMyCoordinatorOption = useCallback(
    (coordinatorOptions = []) => {
      if (!employeeId || !Array.isArray(coordinatorOptions)) return null;
      return (
        coordinatorOptions.find((option) => option.value === employeeId) || null
      );
    },
    [employeeId]
  );

  /**
   * Full name of the logged-in user. Prefers the backend-fetched employee record
   * (first + middle + last name); falls back to the name from the token when the
   * record isn't loaded yet or carries no name parts.
   *
   * @returns {string} The user's full name.
   */
  const getFullName = useCallback(() => {
    const backendName = formatPrintName(
      get(employeeDetails, 'firstName', ''),
      get(employeeDetails, 'middleName', ''),
      get(employeeDetails, 'lastName', '')
    );
    return backendName || tokenManager.getFullName();
  }, [employeeDetails]);

  const roleNames = get(employeeDetails, 'roleNames', []);

  // The caller's single "headline" role — the most senior role they hold (across global +
  // per-office assignments), by the app's role hierarchy. Lets a consumer just ask "what is
  // this user?" without inspecting the whole role structure. Null when nothing is known yet.
  const primaryRole = useMemo(() => {
    const held = [
      ...roleNames,
      ...roles.map((r) => r?.roleName),
      ...officeAssignments.map((o) => o?.roleName),
    ].filter(Boolean);
    let best = null;
    let bestLevel = -1;
    for (const name of held) {
      const level = ROLE_HIERARCHY[name] ?? -1;
      if (level > bestLevel) {
        bestLevel = level;
        best = name;
      }
    }
    return best;
  }, [roleNames, roles, officeAssignments]);

  return {
    employeeId,
    employeeName: getFullName(),
    employeeEmail: get(employeeDetails, 'email', null),
    roleNames,
    officeIds,
    roles,
    officeAssignments,
    primaryRole,
    isLoading,
    isError,
    getMyOfficeOption,
    getMyCoordinatorOption,
    getFullName,
  };
};
